import { TAU, smoothstep } from "./math.ts";

/** Cliff-wall collider columns: squares of half-size CLIFF_HALF every CLIFF_SPACING metres. */
export const CLIFF_SPACING = 2.4;
export const CLIFF_HALF = 2;

/**
 * Hand-authored landforms layered onto an island's analytic heightfield with `max`.
 * Everything is island-local metres, so server collision and client meshes agree.
 */

export interface Wobble {
  k: number;
  amp: number;
  phase: number;
}

/** Flat-topped rock tier: a superellipse plateau ringed by a near-vertical cliff. */
export interface Mesa {
  x: number;
  z: number;
  rx: number;
  rz: number;
  /** Superellipse exponent; higher is boxier. */
  power: number;
  top: number;
  /** Gentle crown added at the centre of the plateau. */
  dome: number;
  /** Horizontal width of the cliff wall, in metres. */
  edge: number;
  wobble: readonly Wobble[];
  /** Trampled sand on top instead of grass (rendering only). */
  sandy?: boolean;
}

/** Straight earthen ramp from (x0, z0, h0) to (x1, z1, h1). */
export interface Ramp {
  x0: number;
  z0: number;
  h0: number;
  x1: number;
  z1: number;
  h1: number;
  width: number;
}

/** Normalised superellipse radius: < 1 inside the cliff line. */
export function mesaShape(m: Mesa, x: number, z: number): number {
  const u = (x - m.x) / m.rx, v = (z - m.z) / m.rz;
  const n = (Math.abs(u) ** m.power + Math.abs(v) ** m.power) ** (1 / m.power);
  return n / mesaWobble(m, Math.atan2(v, u));
}

function mesaWobble(m: Mesa, angle: number): number {
  let f = 1;
  for (const w of m.wobble) f += w.amp * Math.sin(w.k * angle + w.phase);
  return f;
}

/** Cliff-wall width in normalised radius units. */
export function mesaEdge(m: Mesa): number {
  return m.edge / Math.min(m.rx, m.rz);
}

/** Plateau surface at normalised radius `s` (before the cliff falloff). */
export function mesaPlateau(m: Mesa, s: number): number {
  return m.top + m.dome * (1 - s * s);
}

/** Mesa surface height, or -Infinity outside its footprint. */
export function mesaHeight(m: Mesa, x: number, z: number): number {
  const s = mesaShape(m, x, z);
  if (s >= 1) return -Infinity;
  const e = mesaEdge(m);
  return mesaPlateau(m, Math.min(s, 1 - e)) * (1 - smoothstep(1 - e, 1, s));
}

export interface ContourPoint {
  x: number;
  z: number;
  /** Outward unit normal. */
  nx: number;
  nz: number;
}

/**
 * Evenly spaced points on the mesa outline, `inset` metres inside the middle of
 * the cliff wall, ordered counter-clockwise in (x, z) with outward normals.
 */
export function mesaContour(m: Mesa, inset: number, spacing: number): ContourPoint[] {
  const target = 1 - mesaEdge(m) / 2 - inset / Math.min(m.rx, m.rz);
  const dense: { x: number; z: number }[] = [];
  const samples = 2048;
  for (let i = 0; i < samples; i++) {
    const a = (i / samples) * TAU, c = Math.cos(a), s = Math.sin(a);
    const n = (Math.abs(c) ** m.power + Math.abs(s) ** m.power) ** (1 / m.power);
    const r = (target * mesaWobble(m, a)) / n;
    dense.push({ x: m.x + r * c * m.rx, z: m.z + r * s * m.rz });
  }
  const out: ContourPoint[] = [];
  let carried = 0;
  for (let i = 0; i < samples; i++) {
    const a = dense[i]!, b = dense[(i + 1) % samples]!;
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len === 0) continue;
    const tx = (b.x - a.x) / len, tz = (b.z - a.z) / len;
    while (carried <= len) {
      const t = carried / len;
      out.push({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t, nx: tz, nz: -tx });
      carried += spacing;
    }
    carried -= len;
  }
  return out;
}

/** Distance from (x, z) to the ramp centreline and the clamped parameter along it. */
export function rampProjection(r: Ramp, x: number, z: number): { t: number; side: number; beyond: number } {
  const dx = r.x1 - r.x0, dz = r.z1 - r.z0;
  const len = Math.hypot(dx, dz);
  const along = ((x - r.x0) * dx + (z - r.z0) * dz) / len;
  const side = Math.abs((x - r.x0) * dz - (z - r.z0) * dx) / len;
  const beyond = along < 0 ? -along : along > len ? along - len : 0;
  return { t: Math.min(Math.max(along / len, 0), 1), side, beyond };
}

/** Ramp surface with 45° embankments; -Infinity where it cannot matter. */
export function rampHeight(r: Ramp, x: number, z: number): number {
  const p = rampProjection(r, x, z);
  const drop = Math.max(0, p.side - r.width / 2) + p.beyond;
  if (drop > 12) return -Infinity;
  return r.h0 + (r.h1 - r.h0) * p.t - drop;
}

/** Trodden footpath (rendering only): a polyline of island-local points. */
export interface Trail {
  points: readonly { x: number; z: number }[];
  width: number;
}

/** Horizontal distance from (x, z) to the nearest segment of the trail. */
export function trailDistance(t: Trail, x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i + 1 < t.points.length; i++) {
    const a = t.points[i]!, b = t.points[i + 1]!;
    const dx = b.x - a.x, dz = b.z - a.z, len2 = dx * dx + dz * dz;
    const u = len2 > 0 ? Math.min(Math.max(((x - a.x) * dx + (z - a.z) * dz) / len2, 0), 1) : 0;
    best = Math.min(best, Math.hypot(x - a.x - dx * u, z - a.z - dz * u));
  }
  return best;
}
